import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import SalesDataUpload from '../components/SalesDataUpload';
import LifecycleDataUpload from '../components/LifecycleDataUpload';
import StockDataUpload from '../components/StockDataUpload';
import SuppliersDataUpload from '../components/SuppliersDataUpload';
import ArticleSuppliersDataUpload from '../components/ArticleSuppliersDataUpload';
import ArticlesDataUpload from '../components/ArticlesDataUpload';
import InlineEditTable from '../components/InlineEditTable';
import ArticleSelector from '../components/ArticleSelector';
import { articlesCrud, salesCrud, stockCrud, suppliersCrud, articleSuppliersCrud, lifecyclesCrud } from '../services/crudService';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import { useDatabaseCounts } from '../hooks/useDatabaseCounts';
import { useSalesList } from '../hooks/useSalesList';
import { useStockList } from '../hooks/useStockList';
import { useSuppliersList } from '../hooks/useSuppliersList';
import { useArticleSuppliersList } from '../hooks/useArticleSuppliersList';
import { useLifecyclesList } from '../hooks/useLifecyclesList';
import { useFilteredArticles } from '../hooks/useFilteredArticles';
import { ColumnDef } from '../types/table.types';
import { FilterRule } from '../types/api.types';
import { 
  Database, 
  ArrowLeft, 
  TrendingUp, 
  Layers, 
  Package,
  Settings,
  ChevronRight,
  Home,
  Truck,
  Link,
  ShoppingCart,
  Info,
  X,
  Lock,
  Filter as FilterIcon,
  Plus
} from 'lucide-react';

type TabType = 'articles' | 'sales' | 'lifecycles' | 'stock' | 'suppliers' | 'article-suppliers' | 'settings';

interface Tab {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  description: string;
  countKey?: 'articles' | 'sales' | 'lifecycles' | 'stock' | 'suppliers' | 'articleSuppliers';
}

const DatabaseManage: React.FC = () => {
  const params = useParams<{ databaseId: string }>();
  const databaseIdParam = params?.databaseId || null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeDatabase, databases, setActiveDatabase, fetchDatabases } = useDatabaseContext();
  const [activeTab, setActiveTab] = useState<TabType>('articles');
  const [showNoArticlesPopup, setShowNoArticlesPopup] = useState(false);
  const [tablePage, setTablePage] = useState<Record<TabType, number>>({
    articles: 1,
    sales: 1,
    lifecycles: 1,
    stock: 1,
    suppliers: 1,
    'article-suppliers': 1,
    settings: 1,
  });
  const [tablePageSize] = useState(50);
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const { toasts, success, error, removeToast } = useToast();

  // Find the database by ID
  const currentDatabase = useMemo(() => {
    if (!databaseIdParam) return null;
    return databases.find(db => db.id === databaseIdParam) || activeDatabase;
  }, [databaseIdParam, databases, activeDatabase]);

  // Ensure the database is set as active when component loads
  useEffect(() => {
    if (databaseIdParam && currentDatabase && activeDatabase?.id !== databaseIdParam) {
      setActiveDatabase(currentDatabase);
    }
  }, [databaseIdParam, currentDatabase, activeDatabase, setActiveDatabase]);

  // Reset filters and page when switching tabs
  useEffect(() => {
    setFilters([]);
    setShowFilters(false);
    // Reset page to 1 for all tabs when switching
    setTablePage({
      articles: 1,
      sales: 1,
      lifecycles: 1,
      stock: 1,
      suppliers: 1,
      'article-suppliers': 1,
      settings: 1,
    });
  }, [activeTab]);

  // Reset page to 1 when filters change
  useEffect(() => {
    if (filters.length > 0) {
      setTablePage(prev => ({
        ...prev,
        [activeTab]: 1,
      }));
    }
  }, [filters, activeTab]);

  const databaseId = currentDatabase?.id || databaseIdParam || null;
  const databaseName = currentDatabase?.name || null;

  // Fetch counts for all tabs
  const { data: counts, isLoading: countsLoading } = useDatabaseCounts(databaseId);

  // Check if database has articles
  const hasArticles = useMemo(() => {
    return counts && counts.articles !== null && counts.articles > 0;
  }, [counts]);

  // Show popup on initial load if no articles and user is on a locked tab
  useEffect(() => {
    if (!countsLoading && counts && counts.articles === 0 && activeTab !== 'articles' && activeTab !== 'settings') {
      setShowNoArticlesPopup(true);
      setActiveTab('articles'); // Force switch to articles tab
    }
  }, [counts, countsLoading, activeTab]); // Check when counts load or tab changes

  // Handle tab click - show popup if locked, otherwise switch tab
  const handleTabClick = (tabId: TabType) => {
    if (isTabLocked(tabId)) {
      setShowNoArticlesPopup(true);
      return; // Don't change tab
    }
    setActiveTab(tabId);
    setShowNoArticlesPopup(false); // Hide popup if switching to unlocked tab
  };

  // Check if a tab should be locked
  const isTabLocked = (tabId: TabType): boolean => {
    if (tabId === 'articles' || tabId === 'settings') {
      return false; // Articles and settings are always accessible
    }
    return !hasArticles; // Lock other tabs if no articles
  };

  // Helper function to format numbers with commas
  const formatCount = (count: number | null | undefined): string | null => {
    if (count === null || count === undefined) return null;
    return count.toLocaleString('fr-FR');
  };

  // Helper function to format error messages in French
  const formatError = (error: any): string | null => {
    if (!error) return null;
    
    const errorString = String(error);
    const errorObj = error as any;
    
    // Handle AxiosError
    if (errorObj?.isAxiosError || errorString.includes('AxiosError')) {
      const status = errorObj?.response?.status || errorObj?.status;
      const statusText = errorObj?.response?.statusText;
      
      // Handle specific status codes
      if (status === 500) {
        return 'Erreur serveur. Veuillez réessayer plus tard ou contacter le support si le problème persiste.';
      }
      if (status === 404) {
        return 'Ressource introuvable. La base de données ou les données demandées n\'existent pas.';
      }
      if (status === 403) {
        return 'Accès refusé. Vous n\'avez pas les permissions nécessaires pour accéder à cette ressource.';
      }
      if (status === 401) {
        return 'Non authentifié. Veuillez vous reconnecter.';
      }
      if (status === 400) {
        return 'Requête invalide. Veuillez vérifier les paramètres de votre requête.';
      }
      
      // Generic HTTP error
      if (status) {
        return `Erreur HTTP ${status}${statusText ? `: ${statusText}` : ''}. Veuillez réessayer.`;
      }
    }
    
    // Handle network errors
    if (errorString.includes('Network Error') || errorString.includes('network') || errorObj?.code === 'ERR_NETWORK') {
      return 'Erreur de connexion réseau. Vérifiez votre connexion internet et réessayez.';
    }
    
    // Handle timeout errors
    if (errorString.includes('timeout') || errorObj?.code === 'ECONNABORTED') {
      return 'La requête a expiré. Veuillez réessayer.';
    }
    
    // Handle empty database errors (common case)
    if (errorString.includes('empty') || errorString.includes('vide') || errorString.includes('aucune donnée')) {
      return 'Aucune donnée disponible dans cette base de données.';
    }
    
    // Generic error - try to extract meaningful message
    if (errorObj?.message && typeof errorObj.message === 'string') {
      const message = errorObj.message;
      // If it's already a user-friendly message, return it
      if (!message.includes('AxiosError') && !message.includes('Request failed')) {
        return message;
      }
    }
    
    // Default fallback
    return 'Une erreur est survenue lors du chargement des données. Veuillez réessayer.';
  };

  // Helper function to get count for a tab
  const getTabCount = (countKey?: Tab['countKey']): number | null => {
    if (!countKey || !counts) return null;
    return counts[countKey] ?? null;
  };

  const tabs: Tab[] = [
    {
      id: 'articles',
      label: 'Articles',
      icon: <ShoppingCart size={18} />,
      description: 'Importez de nouveaux articles dans votre catalogue',
      countKey: 'articles'
    },
    {
      id: 'sales',
      label: 'Données de Ventes',
      icon: <TrendingUp size={18} />,
      description: 'Importez les données de ventes pour enrichir vos analyses',
      countKey: 'sales'
    },
    {
      id: 'lifecycles',
      label: 'Cycles de Vie',
      icon: <Layers size={18} />,
      description: 'Gérez les statuts et cycles de vie de vos produits',
      countKey: 'lifecycles'
    },
    {
      id: 'stock',
      label: 'Stock',
      icon: <Package size={18} />,
      description: 'Importez et gérez les niveaux de stock de vos articles',
      countKey: 'stock'
    },
    {
      id: 'suppliers',
      label: 'Fournisseurs',
      icon: <Truck size={18} />,
      description: 'Importez et gérez les données de vos fournisseurs',
      countKey: 'suppliers'
    },
    {
      id: 'article-suppliers',
      label: 'Articles Fournisseurs',
      icon: <Link size={18} />,
      description: 'Gérez les liens entre vos articles et vos fournisseurs',
      countKey: 'articleSuppliers'
    },
    {
      id: 'settings',
      label: 'Paramètres',
      icon: <Settings size={18} />,
      description: 'Configuration avancée de la base de données'
      // No countKey for settings tab
    }
  ];

  const handleBackClick = () => {
    navigate('/mes-donnees');
  };

  // Helper function to refresh counts after any upload
  const handleUploadSuccess = () => {
    if (databaseId) {
      // Invalidate and refetch the counts query to update tab counts immediately
      // Note: useDatabaseCounts uses ['databaseCounts', databaseId] as queryKey
      queryClient.invalidateQueries({ 
        queryKey: ['databaseCounts', databaseId] 
      });
      // Explicitly refetch counts to update tab badges and unlock tabs
      queryClient.refetchQueries({ 
        queryKey: ['databaseCounts', databaseId] 
      });
      
      // Invalidate all data list queries to refresh tables
      queryClient.invalidateQueries({ queryKey: ['articles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['salesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['stockList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['suppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['articleSuppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['lifecyclesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['filteredArticles', databaseId] });
      
      // Explicitly refetch the current tab's data to update raw data view immediately
      switch (activeTab) {
        case 'articles':
          queryClient.refetchQueries({ queryKey: ['articles', databaseId] });
          queryClient.refetchQueries({ queryKey: ['filteredArticles', databaseId] });
          break;
        case 'sales':
          queryClient.refetchQueries({ 
            queryKey: ['salesList', databaseId],
            exact: false 
          });
          break;
        case 'stock':
          queryClient.refetchQueries({ 
            queryKey: ['stockList', databaseId],
            exact: false 
          });
          break;
        case 'suppliers':
          queryClient.refetchQueries({ 
            queryKey: ['suppliersList', databaseId],
            exact: false 
          });
          break;
        case 'article-suppliers':
          queryClient.refetchQueries({ 
            queryKey: ['articleSuppliersList', databaseId],
            exact: false 
          });
          break;
        case 'lifecycles':
          queryClient.refetchQueries({ queryKey: ['lifecyclesList', databaseId] });
          break;
      }
      
      // Also refresh the database list
      fetchDatabases();
    }
  };

  // Articles CRUD handlers
  const handleArticleCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await articlesCrud.create(databaseId, data);
      success('Article créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['articles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['filteredArticles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création de l\'article');
      throw err;
    }
  };

  const handleArticleUpdate = async (articleId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      // Filter out computed fields that don't exist in the database
      // 'id' and 'name' are computed by the frontend from 'ref' and 'nom'
      // 'ref' should not be sent in updates (it's the ID and cannot be changed)
      const { id, name, ref, ...dbData } = data;
      await articlesCrud.update(databaseId, String(articleId), dbData);
      success('Article mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['articles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['filteredArticles', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour de l\'article');
      throw err;
    }
  };

  const handleArticleDelete = async (articleId: string | number) => {
    if (!databaseId) return;
    try {
      await articlesCrud.delete(databaseId, String(articleId));
      success('Article supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['articles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['filteredArticles', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression de l\'article');
      throw err;
    }
  };

  // Sales CRUD handlers
  const handleSaleCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await salesCrud.create(databaseId, data);
      success('Vente créée avec succès');
      queryClient.invalidateQueries({ queryKey: ['salesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création de la vente');
      throw err;
    }
  };

  const handleSaleUpdate = async (saleId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await salesCrud.update(databaseId, saleId, data);
      success('Vente mise à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['salesList', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour de la vente');
      throw err;
    }
  };

  const handleSaleDelete = async (saleId: string | number) => {
    if (!databaseId) return;
    try {
      await salesCrud.delete(databaseId, saleId);
      success('Vente supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['salesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression de la vente');
      throw err;
    }
  };

  // Stock CRUD handlers
  const handleStockCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await stockCrud.create(databaseId, data);
      success('Stock créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['stockList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création du stock');
      throw err;
    }
  };

  const handleStockUpdate = async (stockId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await stockCrud.update(databaseId, stockId, data);
      success('Stock mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['stockList', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour du stock');
      throw err;
    }
  };

  const handleStockDelete = async (stockId: string | number) => {
    if (!databaseId) return;
    try {
      await stockCrud.delete(databaseId, stockId);
      success('Stock supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['stockList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression du stock');
      throw err;
    }
  };

  // Suppliers CRUD handlers
  const handleSupplierCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await suppliersCrud.create(databaseId, data);
      success('Fournisseur créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['suppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création du fournisseur');
      throw err;
    }
  };

  const handleSupplierUpdate = async (supplierId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await suppliersCrud.update(databaseId, supplierId, data);
      success('Fournisseur mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['suppliersList', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour du fournisseur');
      throw err;
    }
  };

  const handleSupplierDelete = async (supplierId: string | number) => {
    if (!databaseId) return;
    try {
      await suppliersCrud.delete(databaseId, supplierId);
      success('Fournisseur supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['suppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression du fournisseur');
      throw err;
    }
  };

  // Article-Suppliers CRUD handlers
  const handleArticleSupplierCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await articleSuppliersCrud.create(databaseId, data);
      success('Relation article-fournisseur créée avec succès');
      queryClient.invalidateQueries({ queryKey: ['articleSuppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création de la relation');
      throw err;
    }
  };

  const handleArticleSupplierUpdate = async (articleSupplierId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await articleSuppliersCrud.update(databaseId, articleSupplierId, data);
      success('Relation article-fournisseur mise à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['articleSuppliersList', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour de la relation');
      throw err;
    }
  };

  const handleArticleSupplierDelete = async (articleSupplierId: string | number) => {
    if (!databaseId) return;
    try {
      await articleSuppliersCrud.delete(databaseId, articleSupplierId);
      success('Relation article-fournisseur supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['articleSuppliersList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression de la relation');
      throw err;
    }
  };

  // Lifecycles CRUD handlers
  const handleLifecycleCreate = async (data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await lifecyclesCrud.create(databaseId, data);
      success('Cycle de vie créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['lifecyclesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la création du cycle de vie');
      throw err;
    }
  };

  const handleLifecycleUpdate = async (lifecycleId: string | number, data: Record<string, any>) => {
    if (!databaseId) return;
    try {
      await lifecyclesCrud.update(databaseId, lifecycleId, data);
      success('Cycle de vie mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['lifecyclesList', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la mise à jour du cycle de vie');
      throw err;
    }
  };

  const handleLifecycleDelete = async (lifecycleId: string | number) => {
    if (!databaseId) return;
    try {
      await lifecyclesCrud.delete(databaseId, lifecycleId);
      success('Cycle de vie supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['lifecyclesList', databaseId] });
      queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
    } catch (err: any) {
      error(err?.error || 'Erreur lors de la suppression du cycle de vie');
      throw err;
    }
  };

  // Data fetching hooks for articles (must be declared before useMemo hooks that depend on them)
  // Always use pagination for articles tab
  const filteredArticlesQuery = useFilteredArticles({
    databaseId: databaseId,
    filters: filters,
    search: '',
    page: tablePage.articles,
    pageSize: tablePageSize,
  });

  // Helper to get article row ID (prefer 'ref', fallback to 'id')
  const getArticleRowId = (row: any): string | number => {
    return row.ref || row.id || String(row.ref || row.id);
  };

  // Helper functions for row IDs
  const getSaleRowId = (row: any): string | number => row.id;
  const getStockRowId = (row: any): string | number => row.id;
  const getSupplierRowId = (row: any): string | number => row.id;
  const getArticleSupplierRowId = (row: any): string | number => row.id;
  const getLifecycleRowId = (row: any): string | number => row.id;

  // Helper to determine column types from data
  const getArticleColumnTypes = useMemo(() => {
    const data = filteredArticlesQuery.data?.articles || [];
    if (!data || data.length === 0) return {};
    
    const types: Record<string, 'text' | 'number' | 'date' | 'select'> = {};
    const firstRow = data[0];
    
    Object.keys(firstRow).forEach((key) => {
      const value = (firstRow as any)[key];
      if (key.includes('date') || key.includes('Date')) {
        types[key] = 'date';
      } else if (typeof value === 'number') {
        types[key] = 'number';
      } else {
        types[key] = 'text';
      }
    });
    
    return types;
  }, [filteredArticlesQuery.data]);

  // Determine required columns for articles (all existing columns)
  const getArticleRequiredColumns = useMemo(() => {
    const data = filteredArticlesQuery.data?.articles || [];
    if (!data || data.length === 0) return [];
    
    // Get all column keys except read-only ones
    const firstRow = data[0];
    return Object.keys(firstRow).filter(key => 
      !['id', 'ref', 'name', 'created_at', 'updated_at'].includes(key)
    );
  }, [filteredArticlesQuery.data]);

  // Get filtered article IDs when filters are active
  const filteredArticleIds = useMemo(() => {
    if (filters.length === 0) return undefined;
    return filteredArticlesQuery.data?.articles?.map(a => String(a.id || a.ref)) || [];
  }, [filters, filteredArticlesQuery.data]);

  // Data fetching hooks for tables (must be declared before column definitions)
  const salesQuery = useSalesList(databaseId, { 
    page: tablePage.sales, 
    pageSize: tablePageSize,
    articleIds: filteredArticleIds,
  });
  const stockQuery = useStockList(databaseId, { 
    page: tablePage.stock, 
    pageSize: tablePageSize,
    articleIds: filteredArticleIds,
  });
  const suppliersQuery = useSuppliersList(databaseId, { page: tablePage.suppliers, pageSize: tablePageSize });
  const articleSuppliersQuery = useArticleSuppliersList(databaseId, { 
    page: tablePage['article-suppliers'], 
    pageSize: tablePageSize,
    articleIds: filteredArticleIds,
  });
  const lifecyclesQuery = useLifecyclesList(databaseId, { 
    page: tablePage.lifecycles, 
    pageSize: tablePageSize,
    articleIds: filteredArticleIds,
  });

  // Column definitions for each tab
  const getStockColumns = (): ColumnDef[] => [
    { key: 'article_id', label: 'Référence Article', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'quantity', label: 'Quantité', sortable: true, align: 'right' },
    { key: 'created_at', label: 'Créé le', sortable: true },
    { key: 'updated_at', label: 'Modifié le', sortable: true },
  ];

  const getSuppliersColumns = (): ColumnDef[] => [
    { key: 'name', label: 'Nom', sortable: true },
    { key: 'default_delivery_delay', label: 'Délai de livraison (mois)', sortable: true, align: 'right' },
    { key: 'location_group', label: 'Groupe de localisation', sortable: true },
    { key: 'created_at', label: 'Créé le', sortable: true },
    { key: 'updated_at', label: 'Modifié le', sortable: true },
  ];

  const getArticleSuppliersColumns = (): ColumnDef[] => [
    { key: 'article_ref', label: 'Référence Article', sortable: true },
    { key: 'supplier_name', label: 'Nom Fournisseur', sortable: true },
    { key: 'minimum_order_quantity', label: 'Quantité min. commande', sortable: true, align: 'right' },
    { key: 'unit_volume_m3', label: 'Volume unitaire (m³)', sortable: true, align: 'right' },
    { key: 'quantity_per_pallet', label: 'Quantité par palette', sortable: true, align: 'right' },
    { key: 'created_at', label: 'Créé le', sortable: true },
    { key: 'updated_at', label: 'Modifié le', sortable: true },
  ];

  const getLifecyclesColumns = (): ColumnDef[] => [
    { key: 'article_id', label: 'Référence Article', sortable: true },
    { key: 'status', label: 'Statut', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
  ];

  // Get articles columns dynamically based on database structure
  const getArticlesColumns = useMemo((): ColumnDef[] => {
    const data = filteredArticlesQuery.data?.articles || [];
    if (!data || data.length === 0) {
      return [
        { key: 'id', label: 'Référence', sortable: true },
        { key: 'name', label: 'Nom', sortable: true },
      ];
    }

    // Get all keys from first article
    const firstArticle = data[0] as any;
    const keys = Object.keys(firstArticle);
    
    // Filter out 'features' field if it's an empty array or doesn't exist in database
    // Check if features exists and has actual data in any article
    const hasRealFeatures = data.some((article: any) => 
      article.features && Array.isArray(article.features) && article.features.length > 0
    );
    const filteredKeys = keys.filter(key => {
      // Exclude 'features' if it's empty/undefined or doesn't have real data
      if (key === 'features') {
        return hasRealFeatures;
      }
      // Exclude computed fields: 'id' is computed from 'ref', 'name' is computed from 'nom'
      if (key === 'id' || key === 'name') {
        return false;
      }
      return true;
    });
    
    // Define priority order for common columns (exclude computed fields)
    const priorityOrder = ['ref', 'nom'];
    const otherKeys = filteredKeys.filter(k => !priorityOrder.includes(k));
    const orderedKeys = [...priorityOrder.filter(k => filteredKeys.includes(k)), ...otherKeys];

    return orderedKeys.map(key => ({
      key,
      label: key === 'ref' ? 'Référence' : 
             key === 'nom' ? 'Nom' :
             key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      sortable: true,
      align: typeof firstArticle[key] === 'number' ? 'right' : 'left',
    }));
  }, [filteredArticlesQuery.data]);

  // Get sales columns dynamically (sales data structure varies)
  const getSalesColumns = useMemo((): ColumnDef[] => {
    if (!salesQuery.data?.results || salesQuery.data.results.length === 0) {
      return [
        { key: 'article_id', label: 'Référence Article', sortable: true },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'quantity', label: 'Quantité', sortable: true, align: 'right' },
      ];
    }

    const firstSale = salesQuery.data.results[0] as any;
    const keys = Object.keys(firstSale);
    
    // Priority order for sales
    const priorityOrder = ['article_id', 'ref', 'date', 'quantity'];
    const otherKeys = keys.filter(k => !priorityOrder.includes(k));
    const orderedKeys = [...priorityOrder.filter(k => keys.includes(k)), ...otherKeys];

    return orderedKeys.map(key => ({
      key,
      label: key === 'article_id' || key === 'ref' ? 'Référence Article' :
             key === 'date' ? 'Date' :
             key === 'quantity' ? 'Quantité' :
             key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      sortable: true,
      align: typeof firstSale[key] === 'number' ? 'right' : 'left',
    }));
  }, [salesQuery.data]);

  // Pagination handlers for each tab
  const handlePageChange = (tab: TabType, page: number) => {
    setTablePage(prev => ({
      ...prev,
      [tab]: page,
    }));
  };

  // Helper to get pagination props for a tab
  const getPaginationProps = (tab: TabType, data: any): { pagination?: import('../types/table.types').PaginationProps } => {
    let count: number | undefined;
    let currentPage: number;
    let totalPages: number | undefined;

    switch (tab) {
      case 'articles':
        // Always use pagination for articles (with or without filters)
        if (filteredArticlesQuery.data) {
          count = filteredArticlesQuery.data.count;
          currentPage = tablePage.articles;
          totalPages = filteredArticlesQuery.data.totalPages;
        } else {
          return {};
        }
        break;
      case 'sales':
        count = salesQuery.data?.count;
        currentPage = tablePage.sales;
        totalPages = salesQuery.data?.total_pages;
        break;
      case 'stock':
        count = stockQuery.data?.count;
        currentPage = tablePage.stock;
        totalPages = stockQuery.data?.total_pages;
        break;
      case 'suppliers':
        count = suppliersQuery.data?.count;
        currentPage = tablePage.suppliers;
        totalPages = suppliersQuery.data?.total_pages;
        break;
      case 'article-suppliers':
        count = articleSuppliersQuery.data?.count;
        currentPage = tablePage['article-suppliers'];
        totalPages = articleSuppliersQuery.data?.total_pages;
        break;
      case 'lifecycles':
        count = lifecyclesQuery.data?.count;
        currentPage = tablePage.lifecycles;
        totalPages = lifecyclesQuery.data?.total_pages;
        break;
      default:
        return {};
    }

    if (count === undefined || totalPages === undefined || totalPages <= 1) {
      return {};
    }

    return {
      pagination: {
        currentPage,
        pageSize: tablePageSize,
        totalCount: count,
        totalPages,
        onPageChange: (page: number) => handlePageChange(tab, page),
      },
    };
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'articles':
        return (
          <div className="space-y-6">
            <ArticlesDataUpload 
              databaseId={databaseId} 
              onUploadSuccess={handleUploadSuccess}
            />
            
            {/* Articles Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Articles importés ({formatCount(filteredArticlesQuery.data?.count ?? counts?.articles) || 0})
                  {filters.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (filtrés)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      // Trigger create form in InlineEditTable
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                >
                  <FilterIcon size={16} />
                  {showFilters ? 'Masquer les filtres' : 'Filtrer'}
                </button>
                </div>
              </div>
              {showFilters && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <ArticleSelector
                    onSelectionChange={(filters) => setFilters(filters)}
                    onApplyFilters={(filters) => setFilters(filters)}
                    selectedFilters={filters}
                  />
                </div>
              )}
              <InlineEditTable
                entityType="articles"
                databaseId={databaseId || ''}
                data={filteredArticlesQuery.data?.articles || []}
                columns={getArticlesColumns}
                loading={filteredArticlesQuery.isLoading}
                error={formatError(filteredArticlesQuery.error)}
                emptyMessage={filters.length > 0 ? "Aucun article ne correspond aux filtres" : "Aucun article importé"}
                maxHeight="600px"
                onUpdate={handleArticleUpdate}
                onCreate={handleArticleCreate}
                onDelete={handleArticleDelete}
                getRowId={getArticleRowId}
                readOnlyColumns={['id', 'ref', 'created_at', 'updated_at']}
                columnTypes={getArticleColumnTypes}
                requiredColumns={getArticleRequiredColumns}
                showCreateButton={false}
                {...getPaginationProps('articles', filteredArticlesQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['articles', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['filteredArticles', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  const articleName = row.nom || row.name || row.ref || row.id || 'cet article';
                  return `Supprimer l'article "${articleName}" ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'sales':
        const salesData = salesQuery.data?.results || [];
        return (
          <div className="space-y-6">
            <SalesDataUpload databaseId={databaseId} onUploadSuccess={handleUploadSuccess} />
            
            {/* Sales Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Données de ventes ({formatCount(salesQuery.data?.count) || 0})
                  {filters.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (filtrées)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                  >
                    <FilterIcon size={16} />
                    {showFilters ? 'Masquer les filtres' : 'Filtrer'}
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <ArticleSelector
                    onSelectionChange={(filters) => setFilters(filters)}
                    onApplyFilters={(filters) => setFilters(filters)}
                    selectedFilters={filters}
                  />
                </div>
              )}
              <InlineEditTable
                entityType="sales"
                databaseId={databaseId || ''}
                data={salesData}
                columns={getSalesColumns}
                loading={salesQuery.isLoading}
                error={formatError(salesQuery.error)}
                emptyMessage={filters.length > 0 ? "Aucune donnée de vente ne correspond aux filtres" : "Aucune donnée de vente importée"}
                maxHeight="600px"
                onUpdate={handleSaleUpdate}
                onCreate={handleSaleCreate}
                onDelete={handleSaleDelete}
                getRowId={getSaleRowId}
                editableColumns={['date', 'quantity']}
                readOnlyColumns={['id', 'article_id', 'created_at', 'updated_at']}
                columnTypes={{ date: 'date', quantity: 'number' }}
                requiredColumns={['date', 'quantity']}
                showCreateButton={false}
                {...getPaginationProps('sales', salesQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['salesList', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  return `Supprimer la vente du ${row.date} pour l'article ${row.article_id || row.ref} ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'lifecycles':
        const lifecyclesData = lifecyclesQuery.data?.results || [];
        return (
          <div className="space-y-6">
            <LifecycleDataUpload databaseId={databaseId} onUploadSuccess={handleUploadSuccess} />
            
            {/* Lifecycles Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cycles de vie ({formatCount(lifecyclesQuery.data?.count) || 0})
                  {filters.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (filtrés)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                  >
                    <FilterIcon size={16} />
                    {showFilters ? 'Masquer les filtres' : 'Filtrer'}
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <ArticleSelector
                    onSelectionChange={(filters) => setFilters(filters)}
                    onApplyFilters={(filters) => setFilters(filters)}
                    selectedFilters={filters}
                  />
                </div>
              )}
              <InlineEditTable
                entityType="lifecycles"
                databaseId={databaseId || ''}
                data={lifecyclesData}
                columns={getLifecyclesColumns()}
                loading={lifecyclesQuery.isLoading}
                error={formatError(lifecyclesQuery.error)}
                emptyMessage={filters.length > 0 ? "Aucun cycle de vie ne correspond aux filtres" : "Aucun cycle de vie importé"}
                maxHeight="600px"
                onUpdate={handleLifecycleUpdate}
                onCreate={handleLifecycleCreate}
                onDelete={handleLifecycleDelete}
                getRowId={getLifecycleRowId}
                editableColumns={[]}
                readOnlyColumns={['id', 'article_id', 'status', 'date', 'created_at', 'updated_at', 'previous_status_id', 'next_status_id']}
                columnTypes={{ date: 'date' }}
                requiredColumns={[]}
                showCreateButton={false}
                {...getPaginationProps('lifecycles', lifecyclesQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['lifecyclesList', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  return `Supprimer le cycle de vie pour l'article ${row.article_id} ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'stock':
        const stockData = stockQuery.data?.results || [];
        return (
          <div className="space-y-6">
            <StockDataUpload databaseId={databaseId} onUploadSuccess={handleUploadSuccess} />
            
            {/* Stock Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Données de stock ({formatCount(stockQuery.data?.count) || 0})
                  {filters.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (filtrées)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                  >
                    <FilterIcon size={16} />
                    {showFilters ? 'Masquer les filtres' : 'Filtrer'}
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <ArticleSelector
                    onSelectionChange={(filters) => setFilters(filters)}
                    onApplyFilters={(filters) => setFilters(filters)}
                    selectedFilters={filters}
                  />
                </div>
              )}
              <InlineEditTable
                entityType="stock"
                databaseId={databaseId || ''}
                data={stockData}
                columns={getStockColumns()}
                loading={stockQuery.isLoading}
                error={formatError(stockQuery.error)}
                emptyMessage={filters.length > 0 ? "Aucune donnée de stock ne correspond aux filtres" : "Aucune donnée de stock importée"}
                maxHeight="600px"
                onUpdate={handleStockUpdate}
                onCreate={handleStockCreate}
                onDelete={handleStockDelete}
                getRowId={getStockRowId}
                editableColumns={['date', 'quantity']}
                readOnlyColumns={['id', 'article_id', 'created_at', 'updated_at']}
                columnTypes={{ date: 'date', quantity: 'number' }}
                requiredColumns={['date', 'quantity']}
                showCreateButton={false}
                {...getPaginationProps('stock', stockQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['stockList', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  return `Supprimer le stock du ${row.date} pour l'article ${row.article_id} ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'suppliers':
        return (
          <div className="space-y-6">
            <SuppliersDataUpload databaseId={databaseId} onUploadSuccess={handleUploadSuccess} />
            
            {/* Suppliers Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Fournisseurs ({formatCount(suppliersQuery.data?.count) || 0})
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                </div>
              </div>
              <InlineEditTable
                entityType="suppliers"
                databaseId={databaseId || ''}
                data={suppliersQuery.data?.results || []}
                columns={getSuppliersColumns()}
                loading={suppliersQuery.isLoading}
                error={formatError(suppliersQuery.error)}
                emptyMessage="Aucun fournisseur importé"
                maxHeight="600px"
                onUpdate={handleSupplierUpdate}
                onCreate={handleSupplierCreate}
                onDelete={handleSupplierDelete}
                getRowId={getSupplierRowId}
                editableColumns={['name', 'default_delivery_delay', 'location_group']}
                readOnlyColumns={['id', 'created_at', 'updated_at']}
                columnTypes={{ name: 'text', default_delivery_delay: 'number', location_group: 'text' }}
                requiredColumns={['name']}
                showCreateButton={false}
                {...getPaginationProps('suppliers', suppliersQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['suppliersList', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  return `Supprimer le fournisseur "${row.name}" ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'article-suppliers':
        const articleSuppliersData = articleSuppliersQuery.data?.results || [];
        return (
          <div className="space-y-6">
            <ArticleSuppliersDataUpload databaseId={databaseId} onUploadSuccess={handleUploadSuccess} />
            
            {/* Article-Suppliers Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Relations articles-fournisseurs ({formatCount(articleSuppliersQuery.data?.count) || 0})
                  {filters.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (filtrées)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const event = new CustomEvent('showCreateForm');
                      window.dispatchEvent(event);
                    }}
                    className="hidden px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Ajouter un élément
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                  >
                    <FilterIcon size={16} />
                    {showFilters ? 'Masquer les filtres' : 'Filtrer'}
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <ArticleSelector
                    onSelectionChange={(filters) => setFilters(filters)}
                    onApplyFilters={(filters) => setFilters(filters)}
                    selectedFilters={filters}
                  />
                </div>
              )}
              <InlineEditTable
                entityType="article-suppliers"
                databaseId={databaseId || ''}
                data={articleSuppliersData}
                columns={getArticleSuppliersColumns()}
                loading={articleSuppliersQuery.isLoading}
                error={formatError(articleSuppliersQuery.error)}
                emptyMessage={filters.length > 0 ? "Aucune relation ne correspond aux filtres" : "Aucune relation article-fournisseur importée"}
                maxHeight="600px"
                onUpdate={handleArticleSupplierUpdate}
                onCreate={handleArticleSupplierCreate}
                onDelete={handleArticleSupplierDelete}
                getRowId={getArticleSupplierRowId}
                editableColumns={['supplier_id', 'minimum_order_quantity', 'unit_volume_m3', 'quantity_per_pallet']}
                readOnlyColumns={['id', 'article_ref', 'supplier_name', 'created_at', 'updated_at']}
                columnTypes={{ 
                  supplier_id: 'select', 
                  minimum_order_quantity: 'number', 
                  unit_volume_m3: 'number', 
                  quantity_per_pallet: 'number' 
                }}
                columnOptions={{
                  supplier_id: (suppliersQuery.data?.results || []).map(s => ({
                    value: s.id,
                    label: s.name
                  }))
                }}
                requiredColumns={['supplier_id']}
                showCreateButton={false}
                {...getPaginationProps('article-suppliers', articleSuppliersQuery.data)}
                onRefresh={() => {
                  queryClient.invalidateQueries({ queryKey: ['articleSuppliersList', databaseId] });
                  queryClient.invalidateQueries({ queryKey: ['databaseCounts', databaseId] });
                }}
                deleteConfirmMessage={(row) => {
                  return `Supprimer la relation entre l'article ${row.article_ref} et le fournisseur ${row.supplier_name || row.supplier_id} ? Cette action est irréversible.`;
                }}
              />
            </div>
          </div>
        );
      
      case 'settings':
        return (
          <div className="max-w-2xl">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la base de données
                </label>
                <input
                  type="text"
                  value={databaseName || ''}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-blue-700">
                  Plus d'options de configuration seront disponibles prochainement.
                </p>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    <div className="bg-gray-50" style={{ height: '92vh' }}>
      {/* Simplified Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <button
              onClick={() => navigate('/mes-donnees')}
              className="flex items-center gap-1 hover:text-gray-700 transition-colors"
            >
              <Home size={16} />
              Mes Données
            </button>
            <ChevronRight size={14} />
            <span className="text-gray-900 font-medium">{databaseName}</span>
          </nav>

          {/* Simple Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackClick}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <Database className="text-blue-600" size={28} />
                  {databaseName}
                </h1>
                <p className="text-gray-600 mt-1">
                  Enrichissez votre base de données
                </p>
              </div>
            </div>

            {/* Status Badge */}
            {activeDatabase?.name === databaseName && (
              <div className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Active
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Light Tabs Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-fit">
            {tabs.map((tab) => {
              const count = getTabCount(tab.countKey);
              const formattedCount = formatCount(count);
              const locked = isTabLocked(tab.id);
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  title={locked ? 'Importez d\'abord des articles pour accéder à cet onglet' : undefined}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                    locked
                      ? 'text-gray-400 cursor-pointer opacity-60'
                      : isActive
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  {locked ? <Lock size={14} /> : tab.icon}
                  <span>{tab.label}</span>
                  {formattedCount !== null && tab.countKey && (
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      isActive
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {formattedCount}
                    </span>
                  )}
                  {countsLoading && formattedCount === null && tab.countKey && (
                    <span className="ml-1 w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                  )}
                </button>
              );
            })}
          </nav>
          
          {/* Tab Description */}
          <p className="text-gray-600 mt-3 text-sm">
            {tabs.find(tab => tab.id === activeTab)?.description}
          </p>
        </div>

        {/* Clean Content Area */}
        <div>
          {renderTabContent()}
        </div>
      </div>

      {/* No Articles Popup */}
      {showNoArticlesPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Info className="text-blue-600" size={20} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Aucun article dans la base
                </h3>
              </div>
              <button
                onClick={() => setShowNoArticlesPopup(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Cette base de données ne contient encore aucun article. Veuillez d'abord importer des articles 
              depuis l'onglet "Articles" avant d'accéder aux autres fonctionnalités.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowNoArticlesPopup(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default DatabaseManage; 